import React, { useState, useMemo } from 'react';
import {
  Popover, Box, Typography, MenuItem, ListItemText,
  Divider, InputAdornment, Avatar, TextField
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';
import defaultAvatar from '../../Images/person.jpg';
import { resolveImageUrl } from '../../utils/imageUrl';
import './Tasks/TaskManagement.css';
import './Attendance/Attendance.css';

const TmsUserSelect = ({
  selectedUserIds = [],
  onChange,
  activeUsers = [],
  loading = false,
}) => {
  const [userPopoverAnchor, setUserPopoverAnchor] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return activeUsers;
    const q = userSearchQuery.trim().toLowerCase();
    return activeUsers.filter((u) => {
      const fn = `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
      const un = (u.name || '').toLowerCase();
      const em = (u.email || '').toLowerCase();
      return fn.includes(q) || un.includes(q) || em.includes(q);
    });
  }, [activeUsers, userSearchQuery]);

  const allActiveIds = useMemo(() => activeUsers.map((u) => Number(u.id)), [activeUsers]);
  const isAllSelected = useMemo(() => {
    if (allActiveIds.length === 0) return false;
    const selectedSet = new Set(selectedUserIds.map(Number));
    return allActiveIds.every((id) => selectedSet.has(id));
  }, [allActiveIds, selectedUserIds]);

  const handleToggleAll = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const updated = isAllSelected ? [] : [...allActiveIds];
    if (onChange) {
      onChange(updated);
    }
  };

  const handleToggleUser = (userId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const idNum = Number(userId);
    const selectedNums = selectedUserIds.map(Number);
    const isSelected = selectedNums.includes(idNum);
    const updated = isSelected
      ? selectedNums.filter((id) => id !== idNum)
      : [...selectedNums, idNum];
    if (onChange) {
      onChange(updated);
    }
  };

  // Compute display text for the trigger button, exactly matching TMS
  const displayText = useMemo(() => {
    if (loading) return 'Loading...';
    if (!selectedUserIds || selectedUserIds.length === 0) return 'Select User';
    if (activeUsers.length > 0 && isAllSelected) return 'All Users';
    if (selectedUserIds.length === 1) {
      const u = activeUsers.find((user) => Number(user.id) === Number(selectedUserIds[0]));
      if (u) {
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name;
      }
      return '1 user selected';
    }
    return `${selectedUserIds.length} users selected`;
  }, [loading, selectedUserIds, isAllSelected, activeUsers]);

  return (
    <div
      style={{
        position: 'relative',
        width: 180,
        marginTop: 6,
        marginBottom: 8,
        marginLeft: 32,
        boxSizing: 'border-box'
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Trigger Button - Exactly like Image 2 */}
      <div
        style={{
          height: 38,
          width: '100%',
          padding: '0 12px',
          backgroundColor: '#ffffff',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: loading ? 'wait' : 'pointer',
          boxSizing: 'border-box',
          transition: 'border-color 0.2s ease',
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!loading) {
            setUserPopoverAnchor(e.currentTarget);
          }
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#036dda';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#d1d5db';
        }}
      >
        <span
          style={{
            fontSize: '13px',
            color: selectedUserIds.length === 0 ? '#475569' : '#1e293b',
            fontWeight: selectedUserIds.length > 0 ? 500 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: 6,
            userSelect: 'none'
          }}
        >
          {displayText}
        </span>
        <ExpandMoreIcon style={{ color: '#036dda', fontSize: 22, flexShrink: 0 }} />
      </div>

      {/* Popover - 100% Matching Image 2 */}
      <Popover
        open={Boolean(userPopoverAnchor)}
        anchorEl={userPopoverAnchor}
        onClose={() => {
          setUserPopoverAnchor(null);
          setUserSearchQuery('');
        }}
        disableScrollLock
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: 'attendance-user-popover-paper',
          style: {
            width: 280,
            marginTop: 4,
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Blue Header with Title & Search Bar */}
        <Box p={1.5} className="attendance-user-popover-header" style={{ backgroundColor: '#036dda', padding: '12px 14px' }}>
          <Typography
            variant="subtitle2"
            className="attendance-user-popover-title"
            style={{
              fontWeight: 700,
              marginBottom: 8,
              color: '#ffffff',
              fontSize: '14px',
            }}
          >
            Select User
          </Typography>
          <TextField
            className="common-user-search-field"
            fullWidth
            size="small"
            placeholder="Search users..."
            variant="outlined"
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />

        {/* User Options List */}
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {/* All Users Option */}
          <MenuItem
            className={`attendance-user-option ${isAllSelected ? 'attendance-user-selected' : ''}`}
            selected={isAllSelected}
            onClick={handleToggleAll}
            style={{
              padding: '8px 16px',
              backgroundColor: isAllSelected ? '#036dda' : '#ffffff',
              color: isAllSelected ? '#ffffff' : '#1e293b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ListItemText
              primary="All Users"
              primaryTypographyProps={{
                style: {
                  color: isAllSelected ? '#ffffff' : '#1e293b',
                  fontWeight: isAllSelected ? 600 : 400,
                  fontSize: '13px',
                },
              }}
            />
            {isAllSelected && (
              <CheckIcon
                style={{
                  color: '#ffffff',
                  fontSize: '1.2rem',
                  marginLeft: 'auto',
                }}
              />
            )}
          </MenuItem>

          {/* Active Users */}
          {filteredUsers.map((user, index) => {
            const isSelected = selectedUserIds.map(Number).includes(Number(user.id));
            const displayName = user.firstName
              ? `${user.firstName} ${user.lastName || ''}`.trim()
              : user.name;
            return (
              <MenuItem
                key={user.id || index}
                className={`attendance-user-option ${isSelected ? 'attendance-user-selected' : ''}`}
                selected={isSelected}
                onClick={(e) => handleToggleUser(user.id, e)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSelected ? '#036dda' : '#ffffff',
                  color: isSelected ? '#ffffff' : '#1e293b',
                }}
              >
                <Box display="flex" alignItems="center" width="100%">
                  <Avatar
                    src={(user && user.photo && String(user.photo).trim() !== '') ? resolveImageUrl(user.photo) : defaultAvatar}
                    imgProps={{
                      onError: (e) => {
                        if (e.target.src !== defaultAvatar) {
                          e.target.src = defaultAvatar;
                        }
                      },
                    }}
                    style={{
                      width: 28,
                      height: 28,
                      marginRight: 10,
                      border: isSelected ? '2px solid #ffffff' : 'none',
                      backgroundColor: 'transparent',
                    }}
                  />
                  <ListItemText
                    primary={displayName}
                    primaryTypographyProps={{
                      style: {
                        color: isSelected ? '#ffffff' : '#1e293b',
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: '13px',
                      },
                    }}
                  />
                  {isSelected && (
                    <CheckIcon
                      style={{
                        color: '#ffffff',
                        fontSize: '1.2rem',
                        marginLeft: 'auto',
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            );
          })}

          {filteredUsers.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              {activeUsers.length === 0 ? 'No active users found' : 'No matching users found'}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default TmsUserSelect;
